import { NgIf } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, Observable, take } from 'rxjs';
import { DynamicObservable } from '../../../core/classes/dynamic-observable';
import { TrimmedRequiredValidator } from '../../../core/validators/trimmed-required.validator';
import { ErrorMessagesComponent } from '../error-messages/error-messages.component';

export interface ICloneDialogConfig {
  cancelButtonText: string;
  submitButtonText: string;
  title: string;
  submitHandler: (trimmedName: string) => Observable<unknown>;
}

@Component({
  imports: [
    NgIf,
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatError,
    TranslatePipe,
    ErrorMessagesComponent,
  ],
  selector: 'app-clone-dialog',
  standalone: true,
  template: `
    <h1 mat-dialog-title>{{ data.title }}</h1>
    <div mat-dialog-content [formGroup]="cloneForm">
      <mat-form-field appearance="outline">
        <mat-label>{{ 'field.name' | translate }}</mat-label>
        <input matInput formControlName="name" />
        <mat-error [control]="cloneForm.get('name')"></mat-error>
      </mat-form-field>
    </div>
    <div mat-dialog-actions align="end">
      <button *ngIf="data.cancelButtonText" matButton="tonal" (click)="onDismiss()">
        {{ data.cancelButtonText }}
      </button>
      <button *ngIf="data.submitButtonText" mat-flat-button type="button" (click)="onClone()">
        {{ data.submitButtonText }}
      </button>
    </div>
  `,
})
export class CloneDialogComponent {
  public cloneForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)]),
  });

  private readonly afterSuccess = new DynamicObservable<unknown>(null);

  constructor(
    public dialogRef: MatDialogRef<CloneDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICloneDialogConfig,
  ) {
    this.afterSuccess.obs$
      .pipe(
        filter((payload) => payload !== null),
        takeUntilDestroyed(),
        take(1),
      )
      .subscribe(() => this.dialogRef.close());
  }

  public onDismiss(): void {
    this.dialogRef.close(false);
  }

  public onClone(): void {
    if (this.cloneForm.valid) {
      const rawValue: string = this.cloneForm.get('name')?.value ?? '';
      const trimmedName: string = (rawValue as string).trim();
      this.afterSuccess.switch(this.data.submitHandler(trimmedName));
    }
  }
}
